import { writable } from 'svelte/store';

export const showDataSelectionModal = writable<boolean>(false);

/** What the filter bar knows when it is built. */
export interface SelectionState {
  /** Coming from the assistant, which manages its own filters and pools. */
  aiChatActive: boolean;

  /** Whether a search has been run and not since cleared. */
  hasSearched: boolean;

  /** How many companies are on screen. */
  companyCount: number;

  industryCount: number;
  contactRoleCount: number;
  contactTitleCount: number;
}

/**
 * Whether building the filter bar should open the data-selection picker.
 *
 * # Why this is a function and not four conditions in `onMount`
 *
 * It is the answer to "has this customer chosen anything yet", and getting it
 * wrong is not a subtle failure: too eager and the picker opens over a search
 * somebody already built, which looks like their results were thrown away.
 *
 * It lives here because `FilterBar.svelte` cannot be unit tested — this app has
 * no component test harness, and `vitest.config.ts` leaves SvelteKit out of the
 * test build on purpose. A predicate in a module can be pinned; the same
 * predicate spelled out inside a lifecycle hook cannot.
 *
 * # The mount that is not an arrival
 *
 * `onMount` fires every time the filter bar is *built*, which is more than once
 * per visit: opening a company profile hides the sidebar and destroys it, and
 * coming back builds it again. The emptiness checks alone cannot tell a fresh
 * arrival from that return — a search built from a free-text query has no
 * centroid, no role and no title, and is still a search — so a customer who
 * looked at one company came back to the picker over their own results.
 *
 * `hasSearched` is the distinction, and it is cleared by `resetSelection`, so
 * starting over still opens the picker.
 */
export function shouldOpenDataSelection(state: SelectionState): boolean {
  // The assistant sets up its own selection; opening the picker over it would
  // be a second way to answer a question it is already asking.
  if (state.aiChatActive) return false;

  // Anything on screen is theirs, whether or not it came from the picker's
  // three fields.
  if (state.hasSearched || state.companyCount > 0) return false;

  return state.industryCount === 0 && state.contactRoleCount === 0 && state.contactTitleCount === 0;
}
